/**
 * API client for the HEAT Client Portal
 * Connects to the portal backend at /api/portal/*
 */

import { auth, PortalUser, LoginResponse } from './auth';

// Use the same domain - backend is at client.ourwayfwd.com/api/portal
const API_BASE = 'https://client.ourwayfwd.com/api/portal';

/**
 * Generic API error
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Generic fetch wrapper with authentication
 * For admin users, automatically includes X-Impersonated-Client-ID header
 */
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  
  // Only set Content-Type: application/json when body exists
  const hasBody = options.body !== undefined && options.body !== null;
  
  const headers: Record<string, string> = {
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    ...auth.getAuthHeader(),
    ...(options.headers as Record<string, string>),
  };

  // For admin users, add the impersonated client ID header
  if (auth.isAdminPortalUser()) {
    const selectedClientId = auth.getClientId();
    if (selectedClientId) {
      headers['X-Impersonated-Client-ID'] = selectedClientId;
    }
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized - redirect to login
    if (response.status === 401) {
      auth.logout();
      window.location.href = '/login';
      throw new ApiError('Unauthorized', 401);
    }

    // Parse response
    const data = await response.json();

    // Handle error responses
    if (!response.ok) {
      throw new ApiError(
        data.error || `API Error: ${response.status}`,
        response.status,
        data
      );
    }

    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

/**
 * API client with all portal endpoints
 */
export const api = {
  // ============================================
  // Authentication
  // ============================================

  /**
   * Login with email and password
   * Returns LoginResponse which may include clients list for admin users
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    return request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<{ user: PortalUser }> {
    return request('/auth/me');
  },

  // ============================================
  // Dashboard
  // ============================================

  /**
   * Get dashboard overview data
   */
  async getDashboard(): Promise<{
    client: {
      id: string;
      name: string;
      description: string | null;
    };
    issue_stats: {
      total: number;
      high_priority: number;
      medium_priority: number;
      low_priority: number;
    };
    recent_issues: Array<{
      id: string;
      title: string;
      priority: string;
      created_at: string;
    }>;
    stakeholder_count: number;
  }> {
    return request('/dashboard');
  },

  // ============================================
  // Issues
  // ============================================

  /**
   * Get list of issues with optional filters
   */
  async getIssues(filters?: {
    priority?: string;
    status?: string;
  }): Promise<{
    issues: Array<{
      id: string;
      title: string;
      description: string | null;
      priority: string;
      status: string;
      created_at: string;
      stakeholders?: Array<{
        id: string;
        name: string;
        profile_image: string | null;
        role: string;
        title: string | null;
        organization: string | null;
        category: string | null;
        client_relationship?: {
          relationship: string;
          interest_level: number | null;
          influence_level: number | null;
          notes: string | null;
        };
      }>;
    }>;
  }> {
    const params = new URLSearchParams();
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.status) params.append('status', filters.status);
    
    const query = params.toString();
    return request(`/issues${query ? `?${query}` : ''}`);
  },

  /**
   * Get detailed information about a specific issue
   */
  async getIssue(issueId: string): Promise<{
    issue: {
      id: string;
      title: string;
      description: string | null;
      priority: string;
      status: string;
      created_at: string;
      updated_at: string | null;
    };
  }> {
    return request(`/issues/${issueId}`);
  },

  /**
   * Get mentions (linked articles) for a specific issue
   */
  async getIssueMentions(issueId: string): Promise<{
    issue_id: string;
    issue_title: string;
    mentions: Array<{
      id: string;
      title: string;
      url: string;
      summary: string | null;
      source_name: string;
      published_at: string | null;
      sentiment_score: number | null;
      category: string | null;
      relevance: number | null;
    }>;
    total: number;
  }> {
    return request(`/issues/${issueId}/mentions`);
  },

  // ============================================
  // Stakeholders
  // ============================================

  /**
   * Get list of stakeholders
   */
  async getStakeholders(): Promise<{
    stakeholders: Array<{
      id: string;
      name: string;
      profile_image: string | null;
      title: string | null;
      affiliation: string | null;
      bio: string | null;
      category: string | null;
      relationship: string | null;
      influence_level: string | null;
      interest_level: string | null;
    }>;
  }> {
    return request('/stakeholders');
  },

  /**
   * Get detailed information about a specific stakeholder
   */
  async getStakeholder(stakeholderId: string): Promise<{
    profile: {
      id: string;
      name: string;
      title: string | null;
      category: string | null;
      affiliation: string | null;
      organization: string | null;
      power_type: string | null;
      bio: string | null;
      profile_image: string | null;
      email: string | null;
      phone: string | null;
      contact_info: string | null;
      key_concerns: string | null;
      media_consumption_patterns: string | null;
      notes: string | null;
      created_at: string | null;
      updated_at: string | null;
    };
    relationship_data: {
      influence_level: number | null;
      interest_level: number | null;
      relationship: string | null;
      status: string | null;
      notes: string | null;
      intent: string | null;
      strategy_mode: string | null;
      escalation_level: string | null;
    };
    relationships: Array<{
      id: string;
      stakeholder: {
        id: string;
        name: string;
        title: string | null;
      affiliation: string | null;
      bio: string | null;
        category: string | null;
        profile_image: string | null;
      };
      type: string;
      strength: number | null;
      direction: 'outgoing' | 'incoming';
      created_at: string | null;
    }>;
  }> {
    return request(`/stakeholders/${stakeholderId}`);
  },

  // ============================================
  // Insights
  // ============================================

  /**
   * Get stakeholder insights and analytics
   */
  async getInsights(): Promise<any> {
    return request('/insights');
  },

  // ============================================
  // Monitoring
  // ============================================

  /**
   * Get monitoring articles
   */
  async getMonitoringArticles(
    period: string = "30",
    search: string = "",
    sourceType: string = "all",
    sortBy: string = "published_date",
    sortOrder: string = "desc",
    offset: number = 0,
    limit: number = 25
  ): Promise<any> {
    const params = new URLSearchParams();
    params.append('period', period);
    if (search) params.append('search', search);
    params.append('source_type', sourceType);
    params.append('sort_by', sortBy);
    params.append('sort_order', sortOrder);
    params.append('offset', offset.toString());
    params.append('limit', limit.toString());
    
    return request(`/monitoring/articles?${params.toString()}`);
  },

  /**
   * Get monitoring analytics
   */
  async getMonitoringAnalytics(period: string = "30"): Promise<any> {
    return request(`/monitoring/analytics?period=${period}`);
  },

  /**
   * Get concise, client-scoped Monitoring signals for the current 24-hour window.
   */
  async getMonitoringSignals(): Promise<any> {
    return request('/monitoring/signals');
  },

  /**
   * Get client-scoped publication and social author frequency for the selected period.
   */
  async getMonitoringSourceStats(period: string = "30"): Promise<any> {
    return request(`/monitoring/source-stats?period=${encodeURIComponent(period)}`);
  },

   // ============================================
  // Stakeholder Notes Update
  // ============================================
  /**
   * Update notes for a stakeholder relationship
   */
  async updateStakeholderNotes(stakeholderId: string, notes: string): Promise<{
    success: boolean;
    notes: string | null;
    updated_at: string | null;
    message: string;
  }> {
    return request(`/stakeholders/${stakeholderId}/notes`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  },

  /**
   * Update stakeholder relationship attributes (interest, influence, relationship)
   */
  async updateStakeholderRelationship(
    stakeholderId: string,
    data: {
      interest_level?: number;
      influence_level?: number;
      relationship?: 'supportive' | 'neutral' | 'opposed' | 'unknown';
      change_reason: string;
    }
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      interest_level: number;
      influence_level: number;
      relationship: string;
      updated_at: string;
    };
  }> {
    return request(`/stakeholders/${stakeholderId}/relationship`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Search the global stakeholder directory
   */
  async searchStakeholderDirectory(query: string): Promise<{
    success: boolean;
    results: Array<{
      id: string;
      name: string;
      affiliation: string | null;
      title: string | null;
      category: string | null;
      profile_image: string | null;
    }>;
  }> {
    return request(`/stakeholder-directory/search?q=${encodeURIComponent(query)}`);
  },

  /**
   * Link an existing stakeholder from the global directory to the client
   */
  async linkStakeholder(
    stakeholderId: string,
    data: {
      interest_level: number;
      influence_level: number;
      relationship: 'supportive' | 'neutral' | 'opposed' | 'unknown';
    }
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      stakeholder_id: string;
      interest_level: number;
      influence_level: number;
      relationship: string;
      created_at: string;
    };
  }> {
    return request(`/stakeholders/${stakeholderId}/link`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // ============================================
  // Health Check
  // ============================================
  /**
   * Check API health status
   */
  async healthCheck(): Promise<{
    status: string;
    service: string;
  }> {
    return request('/health');
  },

  // ============================================
  // Decision Board
  // ============================================
  /**
   * Get Decision Board data with all metrics
   */
  async getDecisionBoard(): Promise<{
    success: boolean;
    data: any;
  }> {
    return request('/decision-board');
  },

  /**
   * Export Decision Board as PDF
   */
  async exportDecisionBoard(): Promise<Blob> {
    const url = `${API_BASE}/decision-board/export`;
    const headers: Record<string, string> = {
      ...auth.getAuthHeader(),
    };

    // For admin users, add the impersonated client ID header
    if (auth.isAdminPortalUser()) {
      const selectedClientId = auth.getClientId();
      if (selectedClientId) {
        headers['X-Impersonated-Client-ID'] = selectedClientId;
      }
    }

    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error('Failed to export Decision Board');
    }

    const blob = await response.blob();
    
    // Trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `decision_board_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return blob;
  },

  // ============================================
  // Stakeholder Recommendations
  // ============================================
  /**
   * Get stakeholder recommendations for the current client
   */
  async getRecommendations(): Promise<any> {
    return request("/recommendations");
  },
  /**
   * Generate new stakeholder recommendations
   */
  async generateRecommendations(options: { limit?: number; minScore?: number } = {}): Promise<any> {
    return request("/recommendations/generate", {
      method: "POST",
      body: JSON.stringify(options),
    });
  },
  /**
   * Accept, reject, or dismiss a recommendation
   */
  async actOnRecommendation(recommendationId: string, action: "accept" | "reject" | "dismiss"): Promise<any> {
    return request(`/recommendations/${recommendationId}`, {
      method: "PATCH",
      body: JSON.stringify({ action }),
    });
  },
  /**
   * Get recommendation statistics
   */
  async getRecommendationStats(): Promise<any> {
    return request("/recommendations/stats");
  },

  // ============================================
  // Daily Briefing
  // ============================================
  /**
   * Get the latest daily briefing
   */
  async getLatestDailyBriefing(): Promise<any> {
    return request("/daily-briefing/latest");
  },
  /**
   * Get daily briefing summary for dashboard
   */
  async getDailyBriefingSummary(): Promise<any> {
    return request("/daily-briefing/summary");
  },
  /**
   * Get a specific daily briefing by ID
   */
  async getDailyBriefing(briefingId: string): Promise<any> {
    return request(`/daily-briefing/${briefingId}`);
  },
  /**
   * Get daily briefing history with pagination
   */
  async getDailyBriefingHistory(page: number = 1, limit: number = 10): Promise<any> {
    return request(`/daily-briefing/history?page=${page}&limit=${limit}`);
  },
  /**
   * Mark a daily briefing as viewed
   */
  async markDailyBriefingViewed(briefingId: string): Promise<any> {
    return request(`/daily-briefing/${briefingId}/mark-viewed`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
  /**
   * Download daily briefing as PDF
   */
  async downloadDailyBriefingPDF(briefingId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE}/daily-briefing/${briefingId}/pdf`, {
      headers: {
        'Content-Type': 'application/json',
        ...auth.getAuthHeader(),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new ApiError(
        `Failed to download PDF: ${response.status}`,
        response.status
      );
    }

    return response.blob();
  },
};
